// Command Palette — Ctrl+/ activated floating search-like palette.
// Closes on Escape, blur, or selecting a command.

import { useCallback, useEffect, useRef, useState } from "react";

interface CommandGroup {
  title: string;
  items: CommandItem[];
}

interface CommandItem {
  label: string;
  shortcut?: string;
  action: () => void;
  danger?: boolean;
}

interface Props {
  groups: CommandGroup[];
  onClose: () => void;
}

export function CommandPalette({ groups, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Flatten into searchable list
  const flat = groups.flatMap((g) =>
    g.items.map((item) => ({ ...item, groupTitle: g.title })),
  );

  const filtered = query.trim()
    ? flat.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      )
    : flat;

  useEffect(() => setSelectedIndex(0), [query]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const executeSelected = useCallback(() => {
    if (filtered[selectedIndex]) {
      filtered[selectedIndex].action();
      onClose();
    }
  }, [filtered, selectedIndex, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Escape": onClose(); break;
        case "ArrowDown": e.preventDefault(); setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1)); break;
        case "ArrowUp": e.preventDefault(); setSelectedIndex((i) => Math.max(i - 1, 0)); break;
        case "Enter": e.preventDefault(); executeSelected(); break;
      }
    },
    [onClose, filtered.length, executeSelected],
  );

  useEffect(() => {
    const el = listRef.current?.children[selectedIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="command-palette-input"
          type="text"
          placeholder="Type a command…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {filtered.length === 0 && (
          <div className="command-palette-empty">no matches</div>
        )}
        {filtered.length > 0 && (
          <div className="command-palette-list" ref={listRef}>
            {filtered.map((item, i) => (
              <div
                key={`${item.groupTitle}-${item.label}`}
                className={
                  "command-palette-item" +
                  (i === selectedIndex ? " selected" : "") +
                  (item.danger ? " danger" : "")
                }
                onClick={() => { item.action(); onClose(); }}
                onMouseEnter={() => setSelectedIndex(i)}
              >
                <span className="command-palette-label">{item.label}</span>
                <div className="spacer" />
                <span className="command-palette-group muted small">{item.groupTitle}</span>
                {item.shortcut && (
                  <kbd className="command-palette-shortcut">{item.shortcut}</kbd>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}